import { useEffect, useRef, useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { ArrowUpwardRounded } from "@mui/icons-material";
import { InsightMark } from "../../../Chat/InsightMark";
import { CustomChartSpec } from "../../../Chat/types";
import { makeYFormat } from "../../../Chat/chatChartFormat";
import { getSeriesPalette } from "../../../Dashboard/Charts/constants/chartColors";
import { GREEN, hairline, textPrimary, textSecondary } from "../steps/stepStyles";

/**
 * Scripted playback of an Insight AI answer, for the onboarding's AI beats.
 *
 * Deliberately NOT a faithful replica of the in-app chat: this is a marketing
 * surface, so it shows one thing — a bespoke chart built on request from real
 * company data — and nothing else. The app's real activity strip was cut — at
 * this size it filled the frame with chrome and buried the answer.
 *
 * Frameless by design. It sits directly on the aurora with only a whisper of
 * surface behind it, so it reads as part of the page rather than a screenshot
 * pasted onto it.
 */

/** Captured 2026-11-04 through the real chat tools against live FMP: search_ticker
    resolved "ASML", "AMD" and "Nvidia" to ASML, AMD and NVDA (NASDAQ), and
    compare_chart(gross_margin) returned CHART exactly as below. Each value is
    TTM to the company's latest reported quarter: ASML Q3 2026 (30 Sep 2026),
    AMD Q3 2026 (26 Sep 2026), NVIDIA Q2 FY2027 (26 Jul 2026). The answer cites
    the tool's summary values verbatim. Recapture rather than edit by hand. */
const QUESTION = "Create a chart comparing the gross margins of ASML, AMD and Nvidia";
const ANSWER =
  "NVIDIA leads on gross margin at 74.7% over the last twelve months. ASML (54.3%) and AMD (53.7%) are almost level.";
const CHART: CustomChartSpec = {
  type: "bar",
  title: "Gross Margin (TTM)",
  xLabels: ["ASML", "AMD", "NVDA"],
  series: [{ name: "Gross Margin (TTM)", values: [54.3, 53.7, 74.7] }],
  yFormat: "percent",
};

/** The models actually behind the assistant, via OpenRouter. */
const MODELS = [
  { name: "Gemini", src: "/model-logos/gemini.webp" },
  { name: "Claude", src: "/model-logos/claude.webp" },
];

const TYPE_MS = 32;
const SENT_PAUSE_MS = 420;
const THINKING_MS = 1400;
const STREAM_WORD_MS = 48;
const HOLD_MS = 4200;

type Phase = "typing" | "asking" | "thinking" | "streaming" | "done";

const prefersReducedMotion = (): boolean =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

const TALL_SCREEN = "@media (min-height: 780px)";

const ANSWER_WORDS = ANSWER.split(" ");
const formatValue = makeYFormat(CHART);
const CHART_VALUES = CHART.series[0].values;
const CHART_MAX = Math.max(...CHART_VALUES.map((value) => value ?? 0));
/** Tallest bar, in px. */
const BAR_MAX = 48;

interface AiChatDemoProps {
  /** Shorter thread for screens that also carry a headline and CTAs. */
  compact?: boolean;
}

export const AiChatDemo = ({ compact = false }: AiChatDemoProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const palette = getSeriesPalette(isDark);
  const [phase, setPhase] = useState<Phase>("typing");
  const [typedChars, setTypedChars] = useState(0);
  const [streamedWords, setStreamedWords] = useState(0);
  // Once an answer has landed it stays on screen while the composer retypes,
  // so the loop never flashes an empty panel between cycles.
  const [hasAnswered, setHasAnswered] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fieldRef = useRef<HTMLDivElement>(null);
  const reduced = useRef(prefersReducedMotion());

  // Keep the caret in view exactly as a real input does: while the text fits
  // scrollLeft clamps to 0 and it stays left-aligned; once it overflows the
  // field scrolls to follow the caret.
  useEffect(() => {
    const field = fieldRef.current;
    if (field) field.scrollLeft = field.scrollWidth;
  }, [typedChars, phase]);

  useEffect(() => {
    if (reduced.current) {
      setPhase("done");
      setTypedChars(QUESTION.length);
      setStreamedWords(ANSWER_WORDS.length);
      setHasAnswered(true);
      return;
    }
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  useEffect(() => {
    if (reduced.current) return;
    if (phase === "typing") {
      if (typedChars < QUESTION.length) {
        timer.current = setTimeout(() => setTypedChars((count) => count + 1), TYPE_MS);
      } else {
        timer.current = setTimeout(() => {
          setStreamedWords(0);
          setPhase("asking");
        }, SENT_PAUSE_MS);
      }
      return;
    }
    if (phase === "asking") {
      timer.current = setTimeout(() => setPhase("thinking"), SENT_PAUSE_MS);
      return;
    }
    if (phase === "thinking") {
      timer.current = setTimeout(() => setPhase("streaming"), THINKING_MS);
      return;
    }
    if (phase === "streaming") {
      if (streamedWords < ANSWER_WORDS.length) {
        timer.current = setTimeout(
          () => setStreamedWords((count) => count + 1),
          STREAM_WORD_MS,
        );
      } else {
        setHasAnswered(true);
        setPhase("done");
      }
      return;
    }
    // Loop, so the demo still reads as live if the user lingers.
    timer.current = setTimeout(() => {
      setTypedChars(0);
      setPhase("typing");
    }, HOLD_MS);
  }, [phase, typedChars, streamedWords]);

  const isTyping = phase === "typing";
  // During a retype the previous exchange stays put, frozen complete.
  const holdingPrevious = isTyping && hasAnswered;
  const showThread = !isTyping || hasAnswered;
  const showAnswer = holdingPrevious || phase === "streaming" || phase === "done";
  const wordsShown = holdingPrevious ? ANSWER_WORDS.length : streamedWords;

  return (
    <Box
      sx={{
        width: "100%",
        // Wider than the old frame so the left/right conversation asymmetry
        // has room to read; still fluid, so narrow devices just get less.
        maxWidth: 380,
        // Load-bearing. Every step that hosts this drops it into a flex box,
        // and a flex item defaults to `min-width: auto` — its automatic
        // minimum is its MIN-CONTENT width, which in CSS beats max-width. The
        // composer's nowrap text therefore dragged the whole panel wider as it
        // typed, past both maxWidth and the container. Pinning min-width to 0
        // makes maxWidth authoritative, so the panel is always
        // min(container, 380) and never reflows while typing.
        minWidth: 0,
        // Also load-bearing, and NOT redundant with minWidth above. minWidth
        // only stops THIS box being pushed; it does nothing about the box's
        // min-content leaking upward. The hosting steps wrap this in further
        // flex items that default to `min-width: auto`, so the composer's
        // nowrap text was inflating an ANCESTOR, which then dragged the panel
        // wider with it. Making the panel a scroll container zeroes its
        // min-content contribution, so no wrapper can be pushed by what's
        // inside here. Nothing is meant to escape the panel anyway.
        overflow: "hidden",
        mx: "auto",
        borderRadius: "24px",
        // A whisper of surface — enough to group the thread, not enough to
        // draw an edge. No border, no shadow (flat UI).
        backgroundColor: isDark ? "rgba(255, 255, 255, 0.025)" : "rgba(0, 0, 0, 0.02)",
        // The steps that host this centre their text; a chat thread reads as
        // fake when the assistant's reply is centred, so reset it here.
        textAlign: "left",
        px: 2,
        pt: 2,
        pb: 1.5,
        "@keyframes demoDot": {
          "0%, 80%, 100%": { opacity: 0.2 },
          "40%": { opacity: 0.9 },
        },
        "@keyframes demoRise": {
          from: { opacity: 0, transform: "translateY(6px)" },
          to: { opacity: 1, transform: "none" },
        },
        "@keyframes demoCaret": {
          "0%, 49%": { opacity: 1 },
          "50%, 100%": { opacity: 0 },
        },
        "@media (prefers-reduced-motion: reduce)": {
          "& *": { animation: "none !important" },
        },
      }}
    >
      {/* Fixed height so the demo never grows and pushes the CTA down.
          Top-aligned: the question anchors under the top edge and the answer
          flows down from it, so nothing jumps as words stream in. */}
      <Box
        sx={{
          // Must clear the question bubble, the mark, the chart AND the
          // reply — clipping either would cut the answer itself. Sized
          // against the NARROWEST supported phone (375pt), where the reply
          // wraps to the most lines. Wider phones simply have slack.
          height: compact ? 296 : 300,
          ...(compact ? { [TALL_SCREEN]: { height: 300 } } : {}),
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          gap: 1,
          overflow: "hidden",
        }}
      >
        {showThread && (
          <Box
            sx={{
              alignSelf: "flex-end",
              maxWidth: "84%",
              px: 1.75,
              py: 1,
              borderRadius: "16px 16px 5px 16px",
              backgroundColor: GREEN,
              animation: "demoRise 0.3s ease backwards",
            }}
          >
            <Typography
              sx={{ fontSize: "0.8125rem", fontWeight: 500, color: "#ffffff", lineHeight: 1.45 }}
            >
              {QUESTION}
            </Typography>
          </Box>
        )}

        {/* Minimal thinking indicator — three dots, no card, no label. */}
        {phase === "thinking" && (
          <Box sx={{ display: "flex", gap: 0.6, alignSelf: "flex-start", py: 1, pl: 0.5 }}>
            {[0, 1, 2].map((dot) => (
              <Box
                key={dot}
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  backgroundColor: textSecondary(isDark),
                  animation: `demoDot 1.3s ease-in-out ${dot * 0.16}s infinite`,
                }}
              />
            ))}
          </Box>
        )}

        {showAnswer && wordsShown > 0 && (
          <Box
            sx={{
              // Hugs the left edge and stops short of the right, so the thread
              // reads as a two-sided conversation rather than a centred block.
              alignSelf: "flex-start",
              maxWidth: "94%",
              minWidth: 0,
              animation: "demoRise 0.3s ease backwards",
            }}
          >
            {/* The mark sits ABOVE the reply rather than beside it, so the
                bubble starts hard against the left edge instead of being
                indented by an avatar column — it reads further from the
                user's right-hand bubble, and the reply gains that width for
                its text and chart. */}
            <Box sx={{ display: "flex", mb: 0.75 }}>
              <InsightMark size={20} />
            </Box>

            <Box sx={{ minWidth: 0 }}>

              {/* An actual reply surface, so it reads as the assistant
                  answering rather than loose copy on the panel. */}
              <Box
                sx={{
                  px: 1.5,
                  py: 1.25,
                  borderRadius: "5px 16px 16px 16px",
                  backgroundColor: isDark ? "rgba(255, 255, 255, 0.055)" : "rgba(0, 0, 0, 0.04)",
                }}
              >
                {/* The chart the answer was asked for: compare_chart's own
                    spec, coloured by company and formatted the way the app's
                    chat charts are. It leads the reply, as the chart part does
                    in the app. */}
                <Box
                  sx={{
                    mb: 1.25,
                    pb: 1,
                    borderBottom: `1px solid ${hairline(isDark)}`,
                  }}
                >
                  <Typography
                    sx={{
                      mb: 0.875,
                      fontSize: "0.5625rem",
                      fontWeight: 700,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      color: textSecondary(isDark),
                    }}
                  >
                    {CHART.title}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1.5 }}>
                    {CHART.xLabels.map((label, index) => {
                      const value = CHART_VALUES[index];
                      return (
                        <Box
                          key={label}
                          sx={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          {value !== null && (
                            <Typography
                              sx={{
                                fontSize: "0.625rem",
                                fontWeight: 600,
                                fontVariantNumeric: "tabular-nums",
                                color: textPrimary(isDark),
                              }}
                            >
                              {formatValue(value)}
                            </Typography>
                          )}
                          {value !== null && (
                            <Box
                              sx={{
                                width: "100%",
                                maxWidth: 40,
                                height: (value / CHART_MAX) * BAR_MAX,
                                borderRadius: "3px 3px 0 0",
                                backgroundColor: palette[index % palette.length],
                              }}
                            />
                          )}
                          <Typography
                            sx={{
                              fontSize: "0.5625rem",
                              fontWeight: 700,
                              letterSpacing: "0.02em",
                              color: textSecondary(isDark),
                            }}
                          >
                            {label}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>

                <Typography
                  sx={{ fontSize: "0.8125rem", lineHeight: 1.55, color: textPrimary(isDark) }}
                >
                  {ANSWER_WORDS.slice(0, wordsShown).join(" ")}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {/* Powered by — the credibility line, borrowed from the pattern users
          already recognise on AI products. */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 0.75,
          pt: 1.75,
          pb: 1.5,
        }}
      >
        {/* Caption above, marks below — same micro-caption idiom as the
            chart title, so the two lines read as one system. */}
        <Typography
          sx={{
            fontSize: "0.5625rem",
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: textSecondary(isDark),
          }}
        >
          Powered by
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.75 }}>
          {MODELS.map((model) => (
            <Box key={model.name} sx={{ display: "flex", alignItems: "center", gap: 0.625 }}>
              <Box
                component="img"
                src={model.src}
                alt=""
                aria-hidden
                sx={{ width: 15, height: 15, objectFit: "contain", display: "block" }}
              />
              <Typography
                sx={{
                  fontSize: "0.6875rem",
                  fontWeight: 600,
                  color: textPrimary(isDark),
                  opacity: 0.8,
                }}
              >
                {model.name}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Composer — filled, not outlined, so it reads as part of the panel. */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          pl: 1.75,
          pr: 0.75,
          py: 0.875,
          borderRadius: "999px",
          backgroundColor: isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 0.6)",
          border: `1px solid ${hairline(isDark)}`,
        }}
      >
        {/* A real text field: the text is LEFT-anchored and simply sits there
            until it outgrows the field, at which point the field scrolls to
            keep the caret at the right edge. Driven by scrollLeft rather than
            by right-aligning the row — right-alignment made short strings
            slide in from the left, which no input does. */}
        <Box
          ref={fieldRef}
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            whiteSpace: "nowrap",
          }}
        >
          <Typography
            component="span"
            sx={{
              display: "inline-block",
              whiteSpace: "nowrap",
              fontSize: "0.75rem",
              lineHeight: 1.5,
              color: isTyping ? textPrimary(isDark) : textSecondary(isDark),
            }}
          >
            {isTyping && QUESTION.slice(0, typedChars)}
            {!isTyping && "Ask about any stock…"}
            {isTyping && (
              <Box
                component="span"
                sx={{
                  display: "inline-block",
                  width: "1.5px",
                  height: "0.9em",
                  ml: "2px",
                  verticalAlign: "text-bottom",
                  backgroundColor: GREEN,
                  animation: "demoCaret 1s step-end infinite",
                }}
              />
            )}
          </Typography>
        </Box>
        <Box
          sx={{
            width: 26,
            height: 26,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: GREEN,
            flexShrink: 0,
          }}
        >
          <ArrowUpwardRounded sx={{ fontSize: 16, color: "#ffffff" }} />
        </Box>
      </Box>
    </Box>
  );
};
